/**
 * Helpers for matching furniture dimensions against catalogue model-number
 * dimension specs and for locating model numbers within the nested
 * `useGetModelNumbers` response shape.
 *
 * Used by the "Select model number" modal in the Furniture Sidebar.
 */

import type {
	TCategoriesWithModels,
	TCategoryWithModels,
	TDimensions,
	TModelNumber,
} from "types/modelNumbers.type"

/** Outcome of comparing furniture dimensions to a model's allowed dimensions. */
export type TDimensionTag = "best" | "partial" | "none"

/** Per-axis in-range flags plus the aggregate dimension tag for a single model number. */
export type TMatchResult = {
	inRange: { width: boolean; height: boolean; depth: boolean }
	tag: TDimensionTag
	outOfRangeCount: number
}

/**
 * Tests whether a furniture dimension `value` (inches) satisfies a single
 * catalogue dimension entry. An entry is either:
 *  - an exact value, e.g. `"24"` — matches when `value === 24`
 *  - an inclusive range, e.g. `"20-30"` — matches when `20 <= value <= 30`
 *
 * Whitespace around the entry is tolerated. Min/max in a range may be in
 * either order. Returns `false` for empty / non-numeric / malformed entries.
 */
export const dimensionEntryMatches = (entry: string, value: number): boolean => {
	const trimmed = entry.trim()
	if (trimmed === "") return false
	const rangeMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/)
	if (rangeMatch) {
		const min = Number(rangeMatch[1])
		const max = Number(rangeMatch[2])
		if (Number.isNaN(min) || Number.isNaN(max)) return false
		const lo = Math.min(min, max)
		const hi = Math.max(min, max)
		return value >= lo && value <= hi
	}
	const exact = Number(trimmed)
	if (Number.isNaN(exact)) return false
	return value === exact
}

/**
 * Returns `true` when `value` satisfies *any* of the catalogue's dimension
 * `entries` for one axis (each axis on a model number is an array of allowed
 * values/ranges). A `null` or non-finite `value` always returns `false` —
 * a furniture without that dimension set cannot be considered "in range".
 */
export const dimensionMatches = (entries: string[] | undefined, value: number | null): boolean => {
	if (value === null || !Number.isFinite(value)) return false
	if (!entries || entries.length === 0) return false
	return entries.some((entry) => dimensionEntryMatches(entry, value))
}

/**
 * Compares a furniture's W/H/D against a model number's allowed dimensions
 * and produces a `TMatchResult`:
 *  - 0 axes out of range → `tag: "best"`
 *  - 1 or 2 axes out of range → `tag: "partial"`
 *  - 3 axes out of range → `tag: "none"`
 *
 * A `null` furniture axis counts as out-of-range for that axis.
 */
export const computeMatch = (
	modelDims: TDimensions,
	furniture: { width: number | null; height: number | null; depth: number | null },
): TMatchResult => {
	const inRange = {
		width: dimensionMatches(modelDims.width, furniture.width),
		height: dimensionMatches(modelDims.height, furniture.height),
		depth: dimensionMatches(modelDims.depth, furniture.depth),
	}
	const outOfRangeCount = Number(!inRange.width) + Number(!inRange.height) + Number(!inRange.depth)
	let tag: TDimensionTag = "best"
	if (outOfRangeCount === 3) tag = "none"
	else if (outOfRangeCount > 0) tag = "partial"
	return { inRange, tag, outOfRangeCount }
}

/** Flattens every category node across the `/categories/` response. */
const collectAllCategoryNodes = (data: TCategoriesWithModels | undefined): TCategoryWithModels[] => {
	if (!data) return []
	const out: TCategoryWithModels[] = []
	for (const top of Object.values(data)) {
		for (const list of Object.values(top)) {
			if (Array.isArray(list)) out.push(...list)
		}
	}
	return out
}

/**
 * Builds a `TModelNumber[]` from a single category node returned by
 * `/categories/`. Combines `front_model_numbers`, `model_number_to_id`, and
 * `frontend_model_numbers_to_dimensions` into the shape the modal renders.
 */
const buildModelsFromCategoryNode = (node: TCategoryWithModels): TModelNumber[] => {
	const dims = node.frontend_model_numbers_to_dimensions ?? {}
	const idMap = node.model_number_to_id ?? {}
	return (node.front_model_numbers ?? [])
		.map((modelNumber) => {
			const id = idMap[modelNumber]
			const d = dims[modelNumber]
			if (id === undefined || !d) return null
			const model: TModelNumber = {
				id,
				model_number: modelNumber,
				image_from_stevens_catalogue: null,
				category_hierarchy: {
					"Top Level Category": { id: 0, name: node.top_level_category },
					"Sub Category": { id: 0, name: node.supercategory },
					"Model Category": { id: node.id, name: node.name },
				},
				dimensions: d,
			}
			return model
		})
		.filter((m): m is TModelNumber => m !== null)
}

/**
 * Returns model numbers for the category whose `id` matches `categoryId` in
 * the `/categories/` response. Reliable against name collisions because each
 * node has a unique numeric id matching `furniture.category.id`.
 */
export const findModelNumbersForCategory = (
	data: TCategoriesWithModels | undefined,
	categoryId: number | undefined | null,
): TModelNumber[] => {
	if (!data || categoryId == null) return []
	const node = collectAllCategoryNodes(data).find((n) => n.id === categoryId)
	return node ? buildModelsFromCategoryNode(node) : []
}

/**
 * Returns every model number across every category node. Used when the
 * furniture is in the "Other" top-level category, which has no own id and
 * therefore no category-scoped model list — the user can pick any model.
 * Deduplicated by `id`.
 */
export const findAllModelNumbers = (data: TCategoriesWithModels | undefined): TModelNumber[] => {
	if (!data) return []
	const seen = new Set<number>()
	const all: TModelNumber[] = []
	for (const node of collectAllCategoryNodes(data)) {
		for (const model of buildModelsFromCategoryNode(node)) {
			if (seen.has(model.id)) continue
			seen.add(model.id)
			all.push(model)
		}
	}
	return all
}

/**
 * Normalizes a model number for comparison: trims whitespace, strips wrapping
 * single/double quotes, and lowercases.
 *
 * Why: the backend returns `furniture.detected_model_numbers` wrapped in extra
 * single quotes (e.g. `"'10129'"`), but the catalogue stores them unquoted
 * (`"10129"`). Normalizing both sides lets us compare them reliably.
 */
export const normalizeModelNumber = (value: string): string =>
	value.trim().replace(/^['"]+|['"]+$/g, "").trim().toLowerCase()

/**
 * Returns every catalogue model whose normalized `model_number` matches one
 * of `detected` (also normalized). Searches across all category nodes so
 * ML-detected matches outside the furniture's current category still surface.
 * Deduplicated by `id`.
 */
export const findDetectedModelNumbers = (
	data: TCategoriesWithModels | undefined,
	detected: string[] | undefined,
): TModelNumber[] => {
	if (!data || !detected || detected.length === 0) return []
	const wanted = new Set(detected.map(normalizeModelNumber).filter(Boolean))
	if (wanted.size === 0) return []
	const seen = new Set<number>()
	const matches: TModelNumber[] = []
	for (const node of collectAllCategoryNodes(data)) {
		for (const model of buildModelsFromCategoryNode(node)) {
			if (seen.has(model.id)) continue
			if (wanted.has(normalizeModelNumber(model.model_number))) {
				seen.add(model.id)
				matches.push(model)
			}
		}
	}
	return matches
}

/**
 * Joins a single axis's catalogue dimension entries for display, e.g.
 * `["0", "999", "24-30"]` → `"0,999,24-30"`. Returns an em-dash for
 * empty/missing input.
 */
export const formatDimensionEntries = (entries: string[] | undefined): string => {
	if (!entries || entries.length === 0) return "—"
	return entries.join(",")
}
